<template>
  <div>
    <AppDialog
      class="time_off_request_action_dialog"
      size="large"
      title="Time off request"
      :loading="loadingInitial"
      :show="showDialog"
      append-to-body
      @close="closeDialog"
    >
      <ValidationObserver ref="dialogObserver">
        <div class="main-info">
          <div class="main-fields">
            <div
              v-if="selectedPolicyName"
              class="field policy-field"
            >
              <label class="control-label required">Policy</label>
              <div>{{ selectedPolicyName }}</div>
            </div>
            <div class="field">
              <label class="control-label required">Requested time off</label>
              <div>{{ requestedTimeOffMessage }}</div>
              <small class="time_zone tz_message">{{ tzMessage }}</small>
            </div>
            <div
              v-if="currentMessage"
              class="field"
            >
              <label class="control-label required">Message</label>
              <div class="current-message">{{ currentMessage }}</div>
            </div>
            <div
              v-if="responseForDeny"
              class="field"
            >
              <label class="control-label required">Response for deny</label>
              <div class="current-message">{{ responseForDeny }}</div>
            </div>
            <div
              v-if="hasAttachment"
              class="field mb-0"
            >
              <label class="control-label required">Uploaded file</label>
              <div class="d-flex justify-content-start align-items-center">
                <div class="filename">
                  {{ attachment.metadata.filename }}
                </div>
                <a :href="attachmentUrl">
                  <i class="hi hi-export hi-12 d-flex align-items-center ml-5" />
                </a>
              </div>
            </div>
          </div>
          <div>
            <div
              v-if="status"
              class="field mb-20"
            >
              <label class="control-label required">Status</label>
              <div
                class="time_off_request_status"
                :class="statusSymClass"
              >
                {{ status }}
              </div>
            </div>
            <TimeOffRequestBalance
              :member-avatar="userAvatar"
              :member-name="member.name"
              :policy-name="selectedPolicyName"
              :current-balance="currentBalance"
              :hours-used="hoursUsed"
              :remaining-balance="remainingBalance"
              :pending-approval="pendingApproval"
              :show-next-year-balance="showNextYearBalance"
              :current-balance-next-year="currentBalanceNextYear"
              :holiday-hours="holidayHours"
              :hours-used-next-year="hoursUsedNextYear"
              :remaining-balance-next-year="remainingBalanceNextYear"
              :pending-approval-next-year="pendingApprovalNextYear"
              :holiday-hours-next-year="holidayHoursNextYear"
              :start-at-year="startAtYear"
              :stop-at-year="stopAtYear"
            />
          </div>
        </div>
        <div v-if="shiftCount > 0">
          <div class="form-group shift-count-warning-wrapper small mt-15 mb-0">
            <p class="shift-count-warning mb-0">
              <i class="hi-important mr-5"></i>
              {{ shiftOverlapMessage }}
            </p>
          </div>
        </div>
        <div class="day-off-rules">
          <div class="options">
            <div
              v-if="showDetails"
              class="details text-blue cursor-pointer"
              @click="hideDetails(false)"
            >
              <b>Hide details</b>
              <i class="fa fa-chevron-up"></i>
            </div>
            <div
              v-else
              class="details text-blue cursor-pointer"
              @click="hideDetails(true)"
            >
              <b>Show details</b>
              <i class="fa fa-chevron-down"></i>
            </div>
          </div>
        </div>
        <div
          v-show="showDetails"
          class="table-wrapper"
        >
          <div class="scrollable">
            <AppTable
              v-if="member"
              ref="table"
              :loading="loadingInitial"
              :table-data="paginatedItems"
            >
              <AppTableColumn
                v-slot="{ row }"
                header="Days off"
                name="days_off"
              >
                <div :class="{ 'gray-out': grayOutCondition(row) }">
                  <div class="day-off-row">
                    <span class="date">
                      {{ formatDate(row.date) }}
                    </span>
                    <span
                      v-if="row.holiday"
                      class="small holiday"
                    >
                      <i class="hi hi-gift hi-16 text-blue"></i>
                      <span class="text">
                        <b>{{ holidayAmountUsedHours(row) }}</b>
                        {{ holidayType(row) }}
                      </span>
                    </span>
                  </div>
                </div>
              </AppTableColumn>
              <AppTableColumn
                v-slot="{ row }"
                header="Hours per day"
                name="hours_used"
                header-tooltip="Hours deducted from remaining time off balance and reflected on reports"
                header-tooltip-type="info-filled"
                cell-classes="column-all-day"
                :header-tooltip-size="12"
              >
                <div
                  v-tooltip
                  :class="{ 'gray-out': grayOutCondition(row) }"
                  :title="tooltipPaidRow(row)"
                >
                  <div class="duration-row">
                    {{ amountUsedDuration(row.amount_used) }}
                  </div>
                </div>
              </AppTableColumn>
            </AppTable>
            <div
              v-if="showPagination && !loadingInitial"
              class="pagination-content"
            >
              <AppPagination
                :resource="{ one: 'item', many: 'items' }"
                :pagination="pagination"
                @page-changed="changePage"
              />
            </div>
          </div>
        </div>
        <div
          v-if="showMessageInput"
          class="mt-20"
        >
          <ValidationProvider
            v-slot="{ errors }"
            mode="aggressive"
            rules="required|max:280"
            name="response"
          >
            <div :class="{ 'has-error': errors[0] }">
              <label class="control-label require">Reason for deny*</label>
              <textarea
                id="sendReport_message"
                v-model="message"
                class="form-control"
                placeholder="Enter the reason you are denying this time off request"
              />
              <span class="help-block">{{ errors[0] }}</span>
            </div>
          </ValidationProvider>
        </div>
        <div class="d-flex align-items-end gap-15 flex-row-reverse">
          <AppButton
            v-if="approveButton"
            type="primary"
            class="submit-button"
            :disabled="saving"
            @click="approveAction"
          >
            Approve
          </AppButton>
          <AppButton
            v-if="denyButton"
            type="danger"
            class="submit-button"
            :disabled="saving"
            @click="denyAction"
          >
            Deny
          </AppButton>
          <AppButton
            v-if="editButton"
            @click="editAction"
          >
            Edit
          </AppButton>
          <AppButton
            v-if="showCancelButton"
            @click="closeDialog"
          >
            Cancel
          </AppButton>
        </div>
      </ValidationObserver>
    </AppDialog>

    <TimeOffRequestSetupDialog
      v-if="openEditDialog"
      :organization-id="organizationId"
      :selected-time-off-request-id="Number(timeOffRequestId)"
      :partially-paid-edition="partiallyPaid"
      :redirect-path="redirectPath"
      :current-user-dropdown-object="currentUserDropdownObject"
      show-original-time-zone
      show
      @close="closeDialog"
    />

    <ShiftOverlapConfirmationDialog
      v-if="showShiftOverlapDialog"
      :shift-overlap-message="shiftOverlapMessage"
      :overlap-shift-count="shiftCount"
      :shift-overlap-question="shiftOverlapQuestion"
      :saving="saving"
      @close="closeOverlapConfirmationDialog"
      @open-dialog="openDialog"
      @update-remove-shift-field="handleRemoveShiftField"
    />
  </div>
</template>

<script>
import { TimeOffRequestsResource } from '~/resources';
import TimeOffRequestDialogMixin from '~/mixins/TimeOffRequest/TimeOffRequestDialogMixin';
import TimeOffRequestSetupDialog from '~/components/time_off/TimeOffRequestSetupDialog.vue';
import TimeOffRequestBalance from '~/components/time_off/TimeOffRequestBalance.vue';
import ShiftOverlapConfirmationDialog from '~/components/ShiftOverlapConfirmationDialog.vue';

export default {
  name: 'TimeOffRequestActionDialog',
  components: {
    TimeOffRequestSetupDialog,
    TimeOffRequestBalance,
    ShiftOverlapConfirmationDialog,
  },
  mixins: [TimeOffRequestDialogMixin],
  props: {
    selectedTimeOffRequestId: {
      type: Number,
      default: null,
    },
    show: {
      type: Boolean,
      default: false,
    },
    showOriginalTimeZone: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return this.defaultData();
  },
  computed: {
    statusSymClass() {
      if (this.partiallyPaid) {
        return 'partial-paid';
      }

      return this.statusSym;
    },
    userAvatar() {
      if (!this.member.avatar) {
        return '';
      }

      return this.member.avatar.speck;
    },
    selectedPolicyName() {
      if (!this.selectedPolicy?.name) {
        return '';
      }

      return this.selectedPolicy.name;
    },
    hasAttachment() {
      return Object.keys(this.attachment).length !== 0;
    },
    showCancelButton() {
      return this.action !== 'show';
    },
    showMessageInput() {
      return this.action === 'deny';
    },
  },
  async created() {
    if (this.selectedTimeOffRequestId) {
      this.timeOffRequestId = this.selectedTimeOffRequestId;
      this.action = 'show';
      await this.fetchTimeOffRequest(this.timeOffRequestId, this.action);
    }
  },
  methods: {
    defaultData() {
      return {
        showDialog: this.show,
        timeOffRequestDays: [],
        pagination: {
          current_page: 1,
          total_pages: 1,
          next_page: null,
          prev_page: null,
          limit_value: 7,
          current_count: 0,
          total_count: 0,
          first_page: true,
          last_page: true,
        },
        paginatedItems: [],
        loadingInitial: false,
        member: {},
        holidays: [],
        currentBalance: '0:00',
        hoursUsed: '0:00',
        remainingBalance: '0:00',
        pendingApproval: '0:00',
        showNextYearBalance: false,
        currentBalanceNextYear: '0:00',
        holidayHours: '0:00',
        hoursUsedNextYear: '0:00',
        remainingBalanceNextYear: '0:00',
        pendingApprovalNextYear: '0:00',
        holidayHoursNextYear: '0:00',
        totalAmountUsed: '0:00',
        currentPage: 1,
        selectedPolicy: {},
        startsAt: moment(),
        stopsAt: moment(),
        allDay: true,
        showDetails: true,
        message: null,
        startAtYear: '',
        stopAtYear: '',
        showPagination: false,
        startTime: moment().set({ hour: 0, minute: 0, second: 0 }),
        stopTime: moment().set({ hour: 23, minute: 59, second: 59 }),
        policyType: '',
        shiftCount: 0,
        shiftOverlapMessage: '',
        shiftOverlapQuestion: '',
        showShiftOverlapDialog: false,
        saving: false,
        timeOffRequestId: null,
        status: '',
        currentMessage: '',
        responseForDeny: '',
        requestedTimeOffMessage: '',
        tzMessage: '',
        statusSym: '',
        action: 'approve',
        paid: false,
        openEditDialog: false,
        denyButton: false,
        approveButton: false,
        editButton: false,
        originalTimeZone: null,
        attachment: {},
        attachmentUrl: null,
        currentUserDropdownObject: {},
      };
    },
    closeOverlapConfirmationDialog(value) {
      this.showShiftOverlapDialog = false;

      if (value === 'back-to-previous-dialog') {
        this.showDialog = true;
      } else {
        this.closeDialog();
      }
    },
    tooltipPaidRow(row) {
      if (this.partiallyPaid && this.statusSym === 'approved') {
        return row.paid ? '' : 'This PTO will be paid with next pay period';
      }

      return '';
    },
    grayOutCondition(row) {
      if (this.partiallyPaid && this.statusSym === 'approved') {
        return !row.paid;
      }

      return false;
    },
    openDialog() {
      this.closeOverlapConfirmationDialog();
      this.showDialog = true;
    },
    editAction() {
      this.closeDialog(false);
      if (window.TimeOffRequestSetupDialog) {
        window.TimeOffRequestSetupDialog.$children[0].loadInitialData(this.timeOffRequestId, this.partiallyPaid);
      } else {
        this.openEditDialog = true;
      }
    },
    denyAction() {
      if (this.action === 'show') {
        this.$refs.dialogObserver.reset();
        this.action = 'deny';

        this.fetchTimeOffRequest(this.timeOffRequestId, this.action);
      } else {
        this.updateStatus('deny');
      }
    },
    approveAction() {
      if (this.shiftCount > 0 && (this.action === 'show' || this.action === 'approve')) {
        this.closeDialog(false);
        this.showShiftOverlapDialog = true;

        return;
      }
      this.updateStatus('approve');
    },
    async fetchTimeOffRequest(timeOffRequestId, action) {
      try {
        this.loadingInitial = true;
        const params = { id: timeOffRequestId };
        const response = await TimeOffRequestsResource[action](
          params,
          { current_page_title: document.title, current_url: document.URL },
          { abortReload: true },
        );

        if (response) {
          this.member = response.user;
          this.selectedPolicy = response.time_off_policy;
          this.policyType = response.time_off_policy?.paid ? 'paid' : 'unpaid';
          this.startsAt = moment.tz(response.starts_at, response.original_time_zone);
          this.stopsAt = moment.tz(response.stops_at, response.original_time_zone);
          this.originalTimeZone = response.original_time_zone;
          this.paid = response.paid;
          if (!response.all_day) {
            this.startTime = this.startsAt.clone();
            this.stopTime = this.stopsAt.clone();
          }

          this.requestedTimeOffMessage = response.time_off_requested_message;
          this.currentUserDropdownObject = response.time_off_request_user_dropdown_object;
          this.tzMessage = response.tz_message;
          this.denyButton = response.can_deny;
          this.approveButton = response.can_approve;
          this.editButton = response.can_edit;
          this.partiallyPaid = response.partially_paid;
          this.holidays = this.processHolidays(response.holidays);
          this.timeOffRequestDays = response.time_off_request_days;
          this.status = response.status_name;
          this.allDay = response.all_day;
          this.attachment = response.attachment || {};
          this.attachmentUrl = response.attachment_original_url || '';
          this.statusSym = response.status_sym;
          this.currentMessage = response.message;
          this.responseForDeny = response.response;
          this.showNextYearBalance = false;
          this.currentBalance = this.formatBalanceInformation(response.balances.starting_balance?.current);
          this.hoursUsed = this.formatBalanceInformation(response.balances.amount_used.current);
          this.remainingBalance = this.formatBalanceInformation(response.balances.amount_left?.current);
          this.pendingApproval = this.formatBalanceInformation(response.balances.pending_approval?.current);
          this.holidayHours = this.formatBalanceInformation(response.balances.holiday_hours.current);
          this.startAtYear = this.startsAt.format('YYYY');
          this.stopAtYear = this.stopsAt.format('YYYY');

          // Check if time off request spans across two years by verifying either:
          // 1. starting_balance.next exists (full permission users)
          // 2. amount_used.next exists (partial permission users)
          // This ensures the next year balance is shown even when user has limited permissions
          if (response.balances.starting_balance?.next !== undefined || response.balances.amount_used?.next !== undefined) {
            this.showNextYearBalance = true;
            this.currentBalanceNextYear = this.formatBalanceInformation(response.balances.starting_balance?.next);
            this.hoursUsedNextYear = this.formatBalanceInformation(response.balances.amount_used.next);
            this.remainingBalanceNextYear = this.formatBalanceInformation(response.balances.amount_left?.next);
            this.pendingApprovalNextYear = this.formatBalanceInformation(response.balances.pending_approval?.next);
            this.holidayHoursNextYear = this.formatBalanceInformation(response.balances.holiday_hours.next);
          }
          this.calculatePaginationItems();
          await this.fetchShiftOverlaping();
          this.loadingInitial = false;
        } else {
          throw new Error('Unable to fetch data for the time off request.');
        }
      } catch (error) {
        this.closeDialog();
        await this.$appMessage('Unable to fetch data for the time off request. Try again later.');
      }
    },
    async fetchShiftOverlaping() {
      try {
        const params = {
          organizationId: this.organizationId,
        };
        const data = {
          user_ids: [this.member.id],
          start_date: this.startsAt.format('YYYY-MM-DD'),
          stop_date: this.stopsAt.format('YYYY-MM-DD'),
        };

        if (!this.allDay) {
          data.start_time = this.startTime.format('HH:mm:ss');
          data.stop_time = this.stopTime.format('HH:mm:ss');
          data.timezone = this.originalTimeZone;
        }

        const response = await TimeOffRequestsResource.shiftOverlaps(params, data, { abortReload: true });

        this.shiftCount = response.shift_count;
        this.shiftOverlapMessage = response.shift_count > 0 ? response.warning : '';
        this.shiftOverlapQuestion = response.shift_count > 0 ? response.question : '';
      } catch (error) {
        this.shiftCount = 0;
        this.shiftOverlapMessage = '';
        this.shiftOverlapQuestion = '';
      }
    },
    async updateStatus(newStatus, removeShift = false) {
      try {
        this.saving = true;

        const valid = await this.$refs.dialogObserver.validate();

        if (!valid) {
          return;
        }

        const params = {
          id: this.timeOffRequestId,
          organizationId: this.organizationId,
        };
        const { origin } = window.location;
        const url = new URL(origin + this.redirectPath);

        url.searchParams.delete('showTimeOffRequest');

        const response = await TimeOffRequestsResource.update_status(
          params,
          {
            time_off_request: {
              status: newStatus === 'approve' ? 1 : 2,
              response: this.message,
              remove_shifts: removeShift ? 1 : 0,
            },
            redirect_path: url.pathname + url.search,
            current_page_title: document.title,
            current_url: document.URL,
          },
          { abortReload: true },
        );

        if (response.success) {
          this.closeDialog();
          this.showShiftOverlapDialog = false;
          window.location.assign(response.url);
        } else if (response.errors) {
          await this.$appMessage(response.errors);
        } else {
          throw new Error('Unable to update time off request status');
        }
      } catch (error) {
        console.log(error);
        await this.$appMessage('Unable to update time off request status. Try again later.');
      } finally {
        this.saving = false;
      }
    },
    async loadInitialData(timeOffRequestId, action) {
      Object.assign(this.$data, this.defaultData());
      this.showDialog = true;
      this.timeOffRequestId = timeOffRequestId;
      this.action = action;
      await this.fetchTimeOffRequest(timeOffRequestId, action);
    },
    async changePage(pageNumber) {
      this.calculatePaginationItems(pageNumber);
    },
    closeDialog(emitCloseEvent = true) {
      this.showDialog = false;
      if (this.$refs.dialogObserver) {
        this.$refs.dialogObserver.reset();
      }
      if (emitCloseEvent) {
        this.$emit('close');
      }
    },
    amountUsedDuration(amountUsed) {
      return TimeUtils.format_duration(moment.duration(amountUsed, 'seconds'), false);
    },
    handleRemoveShiftField(_, value) {
      this.updateStatus('approve', value);
    },
  },
};
</script>
